// 효율적인 로깅 시스템
import { getPerformanceConfig, shouldLog } from './performanceConfig.ts';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: any;
}

class Logger {
  private component: string;
  private isDevelopment: boolean;
  private enabledLevels: Set<LogLevel>;
  private performanceConfig: ReturnType<typeof getPerformanceConfig>;

  constructor(component: string) {
    this.component = component;
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.performanceConfig = getPerformanceConfig();
    
    // 환경별 로그 레벨 설정
    if (this.isDevelopment) {
      this.enabledLevels = new Set(['debug', 'info', 'warn', 'error']);
    } else {
      this.enabledLevels = new Set(['warn', 'error']); // 프로덕션에서는 경고와 에러만
    }
  }

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString().substr(11, 12); // HH:mm:ss.sss
    const prefix = this.getLogPrefix(level);
    const componentTag = `[${this.component}]`;
    
    return `${timestamp} ${prefix} ${componentTag} ${message}`;
  }

  private getLogPrefix(level: LogLevel): string {
    switch (level) {
      case 'debug': return '🔍';
      case 'info': return 'ℹ️';
      case 'warn': return '⚠️';
      case 'error': return '❌';
      default: return '📝';
    }
  }

  private shouldLog(level: LogLevel): boolean {
    return this.enabledLevels.has(level);
  }

  debug(message: string, context?: LogContext): void {
    if (!this.shouldLog('debug')) return;
    if (!this.performanceConfig.enableDebugLogs) return;
    
    const logKey = `${this.component}:debug:${message}`;
    if (!shouldLog(logKey, this.performanceConfig)) return;
    
    const formattedMessage = this.formatMessage('debug', message, context);
    if (context) {
      console.log(formattedMessage, context);
    } else {
      console.log(formattedMessage);
    }
  }

  info(message: string, context?: LogContext): void {
    if (!this.shouldLog('info')) return;
    
    const formattedMessage = this.formatMessage('info', message, context);
    if (context) {
      console.info(formattedMessage, context);
    } else {
      console.info(formattedMessage);
    }
  }

  warn(message: string, context?: LogContext): void {
    if (!this.shouldLog('warn')) return;
    
    const formattedMessage = this.formatMessage('warn', message, context);
    if (context) {
      console.warn(formattedMessage, context);
    } else {
      console.warn(formattedMessage);
    }
  }

  error(message: string, context?: LogContext): void {
    if (!this.shouldLog('error')) return;
    
    const formattedMessage = this.formatMessage('error', message, context);
    if (context) {
      console.error(formattedMessage, context);
    } else {
      console.error(formattedMessage);
    }
  }

  // 성능 측정을 위한 타이머
  time(label: string): void {
    if (this.isDevelopment) {
      console.time(`${this.component}:${label}`);
    }
  }

  timeEnd(label: string): void {
    if (this.isDevelopment) {
      console.timeEnd(`${this.component}:${label}`);
    }
  }

  // 그룹 로깅 (관련된 로그들을 묶어서 표시)
  group(label: string): void {
    if (this.isDevelopment) {
      console.group(`🔗 ${this.component}: ${label}`);
    }
  }

  groupEnd(): void {
    if (this.isDevelopment) {
      console.groupEnd();
    }
  }
}

// 로거 팩토리 함수
export const createLogger = (component: string): Logger => {
  return new Logger(component);
};

// 전역 로거 설정
export const setLogLevel = (levels: LogLevel[]): void => {
  // 런타임에서 로그 레벨 변경 (개발 도구용)
  if (typeof window !== 'undefined') {
    (window as any).__LOG_LEVELS__ = levels;
  }
};

// 성능 모니터링을 위한 간단한 헬퍼
export const withPerformanceLog = <T extends (...args: any[]) => any>(
  fn: T,
  name: string,
  logger: Logger
): T => {
  return ((...args: any[]) => {
    logger.time(name);
    const result = fn(...args);
    logger.timeEnd(name);
    return result;
  }) as T;
};

// 비동기 함수용 성능 로깅
export const withAsyncPerformanceLog = <T extends (...args: any[]) => Promise<any>>(
  fn: T,
  name: string,
  logger: Logger
): T => {
  return (async (...args: any[]) => {
    logger.time(name);
    try {
      const result = await fn(...args);
      logger.timeEnd(name);
      return result;
    } catch (error) {
      logger.timeEnd(name);
      logger.error(`${name} failed`, { error });
      throw error;
    }
  }) as T;
};

// ============================================
// Phase 3: 간단한 전역 로거 API
// ============================================

export interface LoggerOptions {
  context?: object;
}

/**
 * 현재 시간을 HH:MM:SS 형식으로 반환
 */
function getTimestamp(): string {
  const now = new Date();
  return now.toTimeString().split(' ')[0]; // HH:MM:SS
}

/**
 * 개발 환경 여부 확인
 */
function isDevelopmentEnv(): boolean {
  return process.env.NODE_ENV === 'development';
}

/**
 * 운영 환경 여부 확인
 */
function isProductionEnv(): boolean {
  return process.env.NODE_ENV === 'production';
}

/**
 * 로그 메시지 포맷팅
 */
function formatLogMessage(level: LogLevel, msg: string): string {
  const timestamp = getTimestamp();
  const levelTag = `[${level.toUpperCase()}]`;
  return `${timestamp} ${levelTag} ${msg}`;
}

/**
 * 간단한 전역 로거 객체
 *
 * @description
 * - 복잡한 Logger 클래스 없이 바로 사용 가능
 * - TDD 테스트와 호환
 *
 * @example
 * import { logger } from '../utils/logger';
 *
 * logger.debug('디버그 메시지');
 * logger.info('정보 메시지');
 * logger.warn('경고 메시지');
 * logger.error('에러 메시지', new Error('에러'));
 */
export const logger = {
  /**
   * 디버그 로그 (개발 환경에서만 출력)
   */
  debug: (msg: string, options?: LoggerOptions): void => {
    if (!isDevelopmentEnv()) return;

    const formattedMsg = formatLogMessage('debug', msg);
    console.log(formattedMsg, options || {});
  },

  /**
   * 정보 로그 (운영 환경 제외)
   */
  info: (msg: string, options?: LoggerOptions): void => {
    if (isProductionEnv()) return;

    const formattedMsg = formatLogMessage('info', msg);
    console.info(formattedMsg, options || {});
  },

  /**
   * 경고 로그 (모든 환경)
   */
  warn: (msg: string, options?: LoggerOptions): void => {
    const formattedMsg = formatLogMessage('warn', msg);
    console.warn(formattedMsg, options || {});
  },

  /**
   * 에러 로그 (모든 환경)
   * @param msg 에러 메시지
   * @param error Error 객체 (선택)
   */
  error: (msg: string, error?: Error): void => {
    const formattedMsg = formatLogMessage('error', msg);
    const logData = error ? { error } : {};
    console.error(formattedMsg, logData);
  },
};